/**
 * Custom Hook for Currency Operations
 * Provides easy access to currency formatting and company currency
 */

import { useContext } from 'react';
import { AppContext } from '../context/AppContext';
import {
  formatCurrency,
  getCurrentCurrency,
  getCurrencyConfig,
  getAntdFormatter,
  getAntdParser,
  getCurrencySymbol,
  getCurrencyAffix,
  formatCurrencyForTable,
  getCurrencyOptions,
  isValidCurrency
} from '../utils/currencyService';

export const useCurrency = () => {
  const { company } = useContext(AppContext);
  
  // Get current company's currency
  const currencyCode = getCurrentCurrency(company);
  const currencyConfig = getCurrencyConfig(currencyCode);
  
  return {
    // Current currency info
    currencyCode,
    currencyConfig,
    currencySymbol: currencyConfig.symbol,
    currencyName: currencyConfig.name,
    
    // Formatting functions with current currency pre-applied
    formatAmount: (amount, options = {}) => formatCurrency(amount, currencyCode, options),
    formatForTable: (amount) => formatCurrencyForTable(amount, currencyCode),
    
    // Ant Design helpers
    antdFormatter: getAntdFormatter(currencyCode),
    antdParser: getAntdParser(currencyCode),
    antdPrefix: getCurrencyAffix(currencyCode, 'prefix'),
    antdSuffix: getCurrencyAffix(currencyCode, 'suffix'),
    
    // Utility functions
    formatCurrency: (amount, currency, options) => formatCurrency(amount, currency || currencyCode, options),
    getCurrencySymbol,
    getCurrencyOptions,
    isValidCurrency,
    
    // For migration - formats with explicit currency
    formatWithCurrency: (amount, currency = currencyCode, options = {}) => 
      formatCurrency(amount, currency, options),
  };
};

export default useCurrency;
